<div
    class="dropdown"
    [class.is-open]="isDropdownOpen"
    [class.has-value]="hasValue"
    [class.is-disabled]="isDisabled"
>
    @if (config.label && shouldShowLabel()) {
        <label class="dropdown__label" [attr.for]="customTrigger ? null : triggerElementId">
            {{ config.label }}
            @if (config.required) {
                <span class="dropdown__required">*</span>
            }
        </label>
    }

    @if (customTrigger) {
        <span class="dropdown__custom-trigger">
            <ng-container
                [ngTemplateOutlet]="customTrigger"
                [ngTemplateOutletContext]="{
                    toggle: toggleDropdown.bind(this),
                    isOpen: isDropdownOpen,
                }"
            ></ng-container>
        </span>
    } @else {
        <button
            type="button"
            class="dropdown__trigger"
            #triggerButton
            [attr.id]="triggerElementId"
            [class.dropdown__trigger--icon]="triggerMode === DropdownTriggerMode.Icon"
            [attr.aria-label]="triggerMode === DropdownTriggerMode.Icon ? triggerAriaLabel : null"
            [attr.aria-expanded]="isDropdownOpen"
            [attr.aria-disabled]="isDisabled"
            aria-haspopup="listbox"
            (click)="onTriggerClick($event)"
        >
            @if (triggerMode === DropdownTriggerMode.Icon) {
                <span class="dropdown__trigger-icon" [class.is-active]="hasValue"
                    ><app-svg-icon name="filter_list" size="lg"
                /></span>
            } @else {
                <span class="dropdown__text">
                    @if (isMultiSelect) {
                        <span class="dropdown__text-value">
                            @if (hasValue) {
                                {{ multiPrimaryText }}
                            } @else {
                                @let placeholderParts =
                                    getPlaceholderParts(config.placeholder ?? '');
                                {{ placeholderParts.text
                                }}<span class="dropdown__placeholder-asterisk">{{
                                    placeholderParts.asterisk
                                }}</span>
                            }
                        </span>

                        @if (multiExtraCount) {
                            <span class="dropdown__count-badge">
                                <span class="dropdown__count-badge-text"
                                    >+{{ multiExtraCount }}</span
                                >
                            </span>
                        }
                    } @else {
                        <span class="dropdown__text-value">
                            @if (hasValue) {
                                {{ displayText }}
                            } @else {
                                @let placeholderParts =
                                    getPlaceholderParts(config.placeholder ?? '');
                                {{ placeholderParts.text
                                }}<span class="dropdown__placeholder-asterisk">{{
                                    placeholderParts.asterisk
                                }}</span>
                            }
                        </span>
                    }
                </span>

                <span class="dropdown__chevron" aria-hidden="true"></span>
            }
        </button>
    }

    <ng-template #panelTemplate>
        <div
            class="dropdown__panel dropdown__panel--floating"
            [class.dropdown__panel--above]="openAbove"
            [class.dropdown__panel--search-active]="config.enableSearch && searchValue.trim()"
            [class.dropdown__panel--create]="!!config.allowCreateNew"
        >
            @if (customPanel) {
                <ng-container
                    [ngTemplateOutlet]="customPanel"
                    [ngTemplateOutletContext]="{ close: closeDropdown.bind(this) }"
                ></ng-container>
            } @else {
                @if (config.enableSearch && !config.allowCreateNew) {
                    <ei-search-bar
                        placeholder="Search..."
                        [(ngModel)]="searchValue"
                        [ngModelOptions]="{ standalone: true }"
                    ></ei-search-bar>
                }

                @if (isMultiSelect && config.showSelectAll && filteredOptions.length) {
                    <button
                        type="button"
                        class="dropdown__option dropdown__option--select-all"
                        (click)="toggleSelectAll()"
                    >
                        <span class="dropdown__option-left">
                            <input
                                type="checkbox"
                                class="dropdown__checkbox"
                                [checked]="isAllSelected"
                                [indeterminate]="isSomeSelected"
                            />
                            <span class="dropdown__option-label">Select all</span>
                        </span>
                    </button>
                }

                @if (!filteredOptions.length && !showCreateNewSection) {
                    <div class="dropdown__no-results">No results found</div>
                }

                @for (option of filteredOptions; track option.value) {
                    <button
                        type="button"
                        class="dropdown__option"
                        [class.is-disabled]="option.isDisabled"
                        [class.is-selected]="isOptionSelected(option)"
                        (click)="toggleSelection(option)"
                    >
                        <span class="dropdown__option-left">
                            @if (isMultiSelect) {
                                <input
                                    type="checkbox"
                                    class="dropdown__checkbox"
                                    [checked]="isOptionSelected(option)"
                                />
                            }

                            @if (option.icon) {
                                <img [src]="option.icon" class="dropdown__option-icon" />
                            }

                            <span class="dropdown__option-label">
                                @if (config.enableSearch && searchValue.trim()) {
                                    @let highlighted =
                                        getHighlightedText(
                                            (option.label ?? option.value).toString()
                                        );
                                    {{ highlighted.before }}<strong>{{ highlighted.match }}</strong
                                    >{{ highlighted.after }}
                                } @else {
                                    {{ option.label ?? option.value }}
                                }
                            </span>
                        </span>

                        @if (!isMultiSelect && isOptionSelected(option)) {
                            <span class="dropdown__check" aria-hidden="true"></span>
                        }
                    </button>
                }

                @if (showCreateNewSection) {
                    <div class="dropdown__separator"></div>
                    <div class="dropdown__create-new">
                        <div class="dropdown__create-new-label">
                            {{ config.createNewLabel || 'Create New' }}
                        </div>
                        <div class="dropdown__create-new-input-group">
                            <input
                                type="text"
                                class="dropdown__create-new-input"
                                [class.dropdown__create-new-input--error]="createNewInvalidMessage"
                                [value]="createNewInputValue"
                                (input)="onNewOptionInputChange($any($event.target).value)"
                                (keydown)="onCreateNewKeyDown($event)"
                                (paste)="onCreateNewPaste($event)"
                                (keydown.enter)="onCreateNew()"
                                placeholder="Enter new value"
                            />
                            <button
                                type="button"
                                class="dropdown__create-new-button"
                                (click)="onCreateNew()"
                                [disabled]="isDisabled || !isCreateNewEnabled"
                            >
                                Create
                            </button>
                        </div>
                        @if (createNewInvalidMessage) {
                            <div class="dropdown__create-new-error">
                                {{ createNewInvalidMessage }}
                            </div>
                        }
                    </div>
                }
            }
        </div>
    </ng-template>
</div>
